import { React, useState, useEffect, createContext } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import styles from "../assets/css/modules/shipmentReport.module.css";
import axiosInstance from "../helpers/AxiosInstance";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Header,
  Title,
  ShipmentsReportsTable,
  ButtonsContainer,
  LoadingButton,
} from "../components/utilities";
import NewPrintBtn from "../components/utilities/NewPrintBtn";

// ---------- STYLED BUTTONS ----------
const Button = styled.button`
  position: relative;
  border: 0.5px solid #a9a1a1;
  border-radius: 5px;

  transition: border 0.5s;
  cursor: pointer;
  margin: 0 5px;
  min-width: 9.5%;
  height: 50px;
  background: ${(props) =>
    props.primary
      ? "linear-gradient(#016fb8, #084581, #0c1b4c)"
      : "transparent"};
  box-shadow: ${(props) => (props.primary ? "0px 3px 6px #0000005e" : "none")};
  font: normal normal 900 15px Cairo;
  color: ${(props) => (props.primary ? "white" : "black")};

  @media (max-width: 1440px) {
    font-size: 12px;
  }

  &:hover {
    border: 2px solid var(--primary-color);
  }
`;

// ---------- CONTEXT ----------
export const selectedContext = createContext();

// ---------- COMPONENT ----------
export default function ShipmentsReport() {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("reports");
  const navigate = useNavigate();
  const instance = axiosInstance();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [refetchKey, setRefetchKey] = useState(1);
  const [fetchingData, setFetchingData] = useState(true);
  const [mergedData, setMergedData] = useState([]);
  const [activeStatus, setActiveStatus] = useState("allShipments");
  const [statusID, setStatusID] = useState(0);

  const categories = [
    { statusID: 0, name: "allShipments" },
    { statusID: 5, name: "delivered" },
    { statusID: 4, name: "heading_to_customer" },
    { statusID: 3, name: "processing" },
    { statusID: 1, name: "new_orders" },
    { statusID: 6, name: "Require_Attention" },
    { statusID: 7, name: "pending_return" },
    { statusID: 8, name: "returned" },
    { statusID: -1, name: "received_payments" },
    { statusID: -2, name: "expected_payments" },
  ];

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const toDate = new Date();
  toDate.setDate(toDate.getDate() + 1);
  toDate.setHours(toDate.getHours() + 3);

  const fetchShipments = async (statusID) => {
    if (!fetchingData) return;
    await delay(300);
    try {
      const data = await instance.post("/GetShipmentsPage2", {
        mainStatusID: statusID,
        FromDate: "2020-04-20T13:23:30.141Z",
        ToDate: toDate.toISOString(),
        pageParam: {
          pageSize: 500,
          pageNumber: refetchKey,
        },
      });

      if (data.data.shipments.length === 0) {
        setFetchingData(false);
      }

      return data.data.shipments.map((shipment) => ({
        ...shipment,
        NetCOD: shipment.COD - shipment.ShipmentFees,
        runnerData: `${shipment.RunnerName} / ${shipment.RunnerMobile}`,
      }));
    } catch (error) {
      setFetchingData(false);
      if (error.response && error.response.status === 500) {
        navigate("/server-error");
      }
    }
  };

  const { refetch } = useQuery(
    ["reports", statusID, refetchKey],
    () => fetchShipments(statusID),
    {
      enabled: false,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 1000,
      staleTime: 1000,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setRefetchKey((prev) => prev + 1);
          setMergedData((prevData) => [...prevData, ...data]);
        }
      },
    }
  );

  useEffect(() => {
    if (statusID !== null && fetchingData) {
      refetch();
    }
  }, [statusID, refetchKey, fetchingData]);

  useEffect(() => {
    const reportParam = searchParams.get("report");
    const category = categories.find((c) => c.name === reportParam);

    if (category && reportParam !== activeStatus) {
      setActiveStatus(reportParam);
      setStatusID(category.statusID);
      setFetchingData(true);
      setMergedData([]);
      setRefetchKey(1);
    }
  }, [searchParams]);

  const changeUrl = (value) => {
    setSearchParams(`report=${value}`);
  };

  const changeActiveButton = (e) => {
    const status = e.target.dataset.status;
    const category = categories.find((c) => c.name === status);
    if (!category) return;
    queryClient.removeQueries("reports");
    setActiveStatus(status);
    setStatusID(category.statusID);
    setFetchingData(true);
    setMergedData([]);
    setRefetchKey(1);
    changeUrl(status);
  };

  return (
    <selectedContext.Provider value={{ selected, setSelected }}>
      <Box
        className={styles.headerContainer}
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Header>
          <Title>{t("shipments_reports")}</Title>
        </Header>
        <Box sx={{ marginTop: "10px", width: "100%" }}>
          <ButtonsContainer disabled={fetchingData}>
            {categories.map((cat, index) => (
              <Button
                isRTL={i18n.language === "ar"}
                isFirst={index === 0}
                isLast={index === categories.length - 1}
                key={cat.name}
                primary={activeStatus === cat.name}
                data-status={cat.name}
                onClick={changeActiveButton}
              >
                {t(cat.name)}
                {activeStatus === cat.name && fetchingData && <LoadingButton />}
              </Button>
            ))}
          </ButtonsContainer>
        </Box>
      </Box>
      <NewPrintBtn
        print="shipments"
        data={selected}
        style={{ position: "absolute", top: "-2px", right: "-10px" }}
      />

      <Box sx={{ width: "100%" }}>
        <ShipmentsReportsTable rows={mergedData} />
      </Box>
    </selectedContext.Provider>
  );
}
